import { useUpdateUser } from '@reflag/react-sdk';
import type * as React from 'react';
import { useSetRecoilState } from 'recoil';

import { Alert, confirm, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { appReloadingMessageAtom } from '../../Application';
import { useNavigation } from '../../navigation';
import { siteCentricOptInAtom } from '../../state';
import { CustomerIOActionHandler } from './CustomerIOActionHandler';
import { siteCentricNavConfirmDialogImage } from './SiteCentricNavConfirmDialogAssets';
import { transitionSiteCentric } from './SiteCentricTransition';

/**
 * Trigger the site-centric navigation confirmation dialog from a Customer.io action.
 */
export function SiteCentricNavConfirmDialog(props: {
    organizationId: string;
    isSiteCentricNavEnabled: boolean;
}): React.ReactElement | null {
    const updateUser = useUpdateUser();
    const { navigate } = useNavigation();
    const setAppReloadingMessage = useSetRecoilState(appReloadingMessageAtom);
    const setSiteCentricOptIn = useSetRecoilState(siteCentricOptInAtom);

    const openDialog = useEventCallback(async (event?: CustomerIoInAppMessageActionEvent) => {
        void confirm({
            compact: true,
            title: 'A new way to navigate GitBook',
            image: {
                src: siteCentricNavConfirmDialogImage,
                resize: false,
                width: '100%',
                height: 'auto',
                alt: 'Preview of a structured documentation sidebar',
                style: { aspectRatio: '552 / 260' },
            },
            description: (
                <>
                    <div className="flex flex-col gap-2">
                        {props.isSiteCentricNavEnabled ? (
                            <p>Welcome to your new site-centric navigation!</p>
                        ) : null}
                        <p>
                            Everything used to live together under your organization. Now, your
                            content is organized around your sites.
                        </p>
                        <p>
                            Your sidebar now mirrors your published site. Open a site and you'll
                            find its content, Change Requests, Customization, Insights, and Settings
                            and more all in one place; so every site action is easier to find, and
                            you get full focus on the content, analytics, and work for the site
                            you're on.
                        </p>
                    </div>
                    <Alert
                        kind="default"
                        size="small"
                        className="mt-4"
                        details="Manage this setting from your organization menu."
                    />
                </>
            ),
            confirmLabel: props.isSiteCentricNavEnabled ? 'Close' : 'Try it now',
            onConfirm: async (_event, { preventClose }) => {
                if (props.isSiteCentricNavEnabled) {
                    // If the user has already enabled site-centric navigation, just close the dialog without doing anything.
                    return;
                }

                await toast.catch(
                    async () => {
                        await transitionSiteCentric({
                            navigate,
                            organizationId: props.organizationId,
                            reloadingMessage: 'Getting your new experience ready',
                            setAppReloadingMessage,
                            setSiteCentricOptIn,
                            siteCentricOptIn: 'true',
                            updateUser,
                        });
                    },
                    'Error while enabling the new site-centric navigation',
                    {
                        onReject: () => {
                            preventClose();
                        },
                    }
                );
            },
            cancelLabel: props.isSiteCentricNavEnabled ? null : 'Close',
        });

        event?.detail?.message?.dismiss?.();
    });

    return (
        <CustomerIOActionHandler actionName="show-site-centric-nav-dialog" onAction={openDialog} />
    );
}
